import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import { WebSocketMessage } from './events';
import useWebSocketConnection from './index';

/**
 * Hook that connects to WebSocket only when user is authenticated
 * Automatically handles authentication changes
 */
export const useAuthenticatedWebSocket = ({
	onMessage,
	onOpen,
	onClose,
	onError,
	additionalOptions = {},
}: {
	onMessage?: (data: WebSocketMessage) => void;
	onOpen?: () => void;
	onClose?: (event: CloseEvent) => void;
	onError?: (event: Event) => void;
	additionalOptions?: Record<string, any>;
}) => {
	// Get access token directly from cookie instead of auth store
	const getAccessToken = () => Cookies.get('accessToken') || null;
	const [accessToken, setAccessToken] = useState<string | null>(getAccessToken());
	const [isAuthenticated, setIsAuthenticated] = useState(false);

	// Check for token changes periodically (cookie can be updated from other tabs/sources)
	useEffect(() => {
		// Initial check
		const token = getAccessToken();
		setAccessToken(token);
		setIsAuthenticated(!!token);

		// Set up interval to check for cookie changes
		const intervalId = setInterval(() => {
			const currentToken = getAccessToken();
			if (currentToken !== accessToken) {
				setAccessToken(currentToken);
				setIsAuthenticated(!!currentToken);
			}
		}, 5000); // Check every 5 seconds

		return () => clearInterval(intervalId);
	}, [accessToken]);

	// Custom onOpen handler that checks authentication
	const handleOpen = () => {
		console.log('Authenticated WebSocket connection established');
		onOpen?.();
	};

	// Custom onClose handler that checks if it was due to authentication
	const handleClose = (event: CloseEvent) => {
		// If we have a token but connection closed, it might be an auth issue
		if (accessToken && event.code === 1008) {
			console.error('WebSocket closed due to authentication failure');
		}
		onClose?.(event);
	};

	// Custom onError handler that helps debug auth issues
	const handleError = (event: Event) => {
		if (!accessToken) {
			console.error('WebSocket error: No authentication token available');
		}
		onError?.(event);
	};

	// Only connect if authenticated
	const wsConnection = useWebSocketConnection({
		onMessage,
		onOpen: handleOpen,
		onClose: handleClose,
		onError: handleError,
		additionalOptions: {
			...additionalOptions,
			// Only attempt to connect if authenticated
			retryOnError: isAuthenticated,
			shouldReconnect: (closeEvent: CloseEvent) => {
				// Don't reconnect on auth failures (code 1008)
				if (closeEvent.code === 1008) return false;
				return isAuthenticated;
			},
		},
	});

	return {
		...wsConnection,
		isAuthenticated,
	};
};

export default useAuthenticatedWebSocket;
